import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { updateFoodItem } from '../services/foodItemService';
import './FoodDetailModal.css';

const FoodDetailModal = ({ food, isOpen, onClose, onAddToCart, isOwner, onUpdate }) => {
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);
  const [isFitToScreen, setIsFitToScreen] = useState(true);
  const { showError, showSuccess } = useToast();

  // Ingredient editing (owner-only)
  const [isEditingIngredients, setIsEditingIngredients] = useState(false);
  const [ingredientsText, setIngredientsText] = useState('');
  const [isSavingIngredients, setIsSavingIngredients] = useState(false);
  const [localFood, setLocalFood] = useState(food);

  useEffect(() => {
    setLocalFood(food);
    setIngredientsText(Array.isArray(food?.ingredients) ? food.ingredients.join(', ') : (food?.ingredients || ''));
  }, [food]);

  const handleSaveIngredients = async () => {
    if (!isOwner) return;
    setIsSavingIngredients(true);
    try {
      const cleaned = ingredientsText.split(',').map(i => i.trim()).filter(Boolean).join(', ');
      await updateFoodItem(food.id, { ingredients: cleaned });
      const updatedIngredientsArr = cleaned ? cleaned.split(',').map(i => i.trim()) : [];
      const updatedFood = { ...food, ingredients: updatedIngredientsArr };
      setLocalFood(updatedFood);
      setIsEditingIngredients(false);
      showSuccess('Ingredients updated');
      if (typeof onUpdate === 'function') onUpdate(updatedFood);
    } catch (err) {
      console.error('Failed to update ingredients', err);
      showError(err, 'Failed to update ingredients');
    } finally {
      setIsSavingIngredients(false);
    }
  };

  // Reset image index and fit mode when modal opens - MUST be before early return
  React.useEffect(() => {
    if (isOpen) {
      setCurrentImageIndex(0);
      setIsImageFullscreen(false);
      setIsFitToScreen(true);
    }
  }, [isOpen, food]);

  // Close fullscreen on Escape key
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && isImageFullscreen) {
        setIsImageFullscreen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isImageFullscreen]);

  if (!isOpen || !food) return null;

  // Get images array, fallback to single image or empty array
  const images = food.images && food.images.length > 0
    ? food.images
    : (food.image ? [food.image] : []);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Format pickup time string into a human-readable date/time. Falls back to the original string when parsing fails.
  const formatPickupTime = (timeString) => {
    if (!timeString || timeString === 'Not specified') return 'Not specified';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return timeString;
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }) + ', ' + date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  const handleAddToCart = () => {
    // Prevent adding sold-out or expired items
    const isExpired = food.cutoffTime && new Date(food.cutoffTime) < new Date();
    if (food.ordersLeft === 0) {
      showError('This item is sold out and cannot be added to cart.');
      return;
    }
    if (isExpired) {
      showError('The ordering period for this item has ended.');
      return;
    }

    // Prevent vendors from adding their own items
    if (isOwner) {
      showError('You cannot add your own food items to the cart.');
      return;
    }

    // Prepare cart item data for database
    const cartItem = {
      foodId: food.id,
      foodName: food.name,
      price: food.price,
      quantity: quantity,
      vendorId: food.vendorId,
      vendorName: food.vendorName,
      orderBy: food.orderBy,
      pickupTime: food.pickupTime,
      pickupLocation: food.pickupLocation,
      image: food.image,
      addedAt: new Date().toISOString()
    };

    console.log('Adding to cart:', cartItem);

    // TODO: Connect to database/cart service
    // Example:
    // await cartService.addItem(userId, cartItem);

    if (onAddToCart) {
      onAddToCart(cartItem);
    }

    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <button className="modal-close-btn" onClick={onClose}>×</button>

        {/* Food Image Gallery */}
        <div className="modal-image-container">
          {images.length > 0 ? (
            <div className="modal-image-gallery">
              <img
                src={images[currentImageIndex]}
                alt={`${food.name} - Image ${currentImageIndex + 1}`}
                className="modal-food-image"
                onClick={() => setIsImageFullscreen(true)}
                style={{ cursor: 'zoom-in' }}
              />

              <button
                className="modal-image-expand-btn"
                onClick={() => setIsImageFullscreen(true)}
                aria-label="Open image fullscreen"
                title="Open image fullscreen"
              >
                ⤢
              </button>

              {images.length > 1 && (
                <>
                  <button className="modal-gallery-nav-btn modal-prev-btn" onClick={prevImage}>
                    ‹
                  </button>
                  <button className="modal-gallery-nav-btn modal-next-btn" onClick={nextImage}>
                    ›
                  </button>

                  <div className="modal-image-counter">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="modal-food-image-placeholder">
              <span>🍽️</span>
            </div>
          )}
          {food.availability === 'available' && (
            <div className="availability-badge">Available now</div>
          )}
        </div>

        {/* Food Details */}
        <div className="modal-body">
          <div className="modal-category">{food.category || 'Lunch'}</div>

          <div className="modal-header-row">
            <h2 className="modal-food-name">{food.name}</h2>
            <div className="modal-price">RM {food.price}</div>
          </div>

          <p className="modal-description">{food.description}</p>

          {/* Ingredients */}
          {(localFood?.ingredients && localFood.ingredients.length > 0) || isOwner ? (
            <div className="modal-section">
              <h3 className="modal-section-title">
                Ingredients
                {isOwner && !isEditingIngredients && (
                  <button
                    className="edit-ingredients-btn"
                    onClick={() => {
                      setIngredientsText(Array.isArray(localFood.ingredients) ? localFood.ingredients.join(', ') : (localFood.ingredients || ''));
                      setIsEditingIngredients(true);
                    }}
                  >
                    Edit
                  </button>
                )}
              </h3>

              {isEditingIngredients ? (
                <div className="ingredients-edit">
                  <textarea
                    className="modal-ingredients-textarea"
                    value={ingredientsText}
                    onChange={(e) => setIngredientsText(e.target.value)}
                    placeholder="List ingredients separated by commas"
                  />
                  <div className="ingredients-edit-actions">
                    <button className="save-ingredients-btn" onClick={handleSaveIngredients} disabled={isSavingIngredients}>
                      {isSavingIngredients ? 'Saving...' : 'Save'}
                    </button>
                    <button className="cancel-ingredients-btn" onClick={() => { setIsEditingIngredients(false); setIngredientsText(Array.isArray(localFood.ingredients) ? localFood.ingredients.join(', ') : (localFood.ingredients || '')); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                localFood?.ingredients && localFood.ingredients.length > 0 ? (
                  <ul className="modal-ingredients-list">
                    {localFood.ingredients.map((ingredient, index) => (
                      <li key={index}>{ingredient}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-ingredients">No ingredients listed.</p>
                )
              )}
            </div>
          ) : null}

          {/* Vendor Info */}
          <div className="modal-vendor-info">
            <span>
              Prepared by {
                food.vendorId ? (
                  <Link to={`/vendors/${food.vendorId}/reviews`} className="vendor-link"><strong>{food.vendorName}</strong></Link>
                ) : (
                  <strong>{food.vendorName}</strong>
                )
              }
            </span>
            {food.rating && (
              <span className="modal-rating">
                ⭐ {food.rating}
              </span>
            )}
          </div>

          {/* Order & Pickup Details */}
          <div className="modal-details-grid">
            <div className="modal-detail-item">
              <span className="detail-label">Order by:</span>
              <span className="detail-value">{food.orderBy}</span>
            </div>
            <div className="modal-detail-item">
              <span className="detail-label">Pickup:</span>
              <span className="detail-value">{food.pickupLocation}</span>
            </div>
            <div className="modal-detail-item">
              <span className="detail-label">Pickup time:</span>
              <span className="detail-value">{formatPickupTime(food.pickupTime)}</span>
            </div>
          </div>

          {/* Dietary Labels & Stock */}
          <div className="modal-badges-row">
            {Array.isArray(food.dietaryLabel) ? (
              food.dietaryLabel.map((label, index) => (
                <span key={index} className="dietary-badge">
                  {label.toLowerCase() === 'vegetarian' ? '✓ ' : ''}
                  {label.charAt(0).toUpperCase() + label.slice(1)}
                </span>
              ))
            ) : (
              food.isVegetarian && (
                <span className="dietary-badge">✓ Vegetarian</span>
              )
            )}
            {/* Show low-stock warning only when > 0 and <=5 */}
            {food.ordersLeft !== undefined && food.ordersLeft > 0 && food.ordersLeft <= 5 && (
              <span className="stock-warning">Only {food.ordersLeft} orders left</span>
            )}
            {/* Show explicit status badges */}
            {food.ordersLeft === 0 && (food.cutoffTime && new Date(food.cutoffTime) < new Date()) ? (
              <span className="unavailable-badge">Unavailable</span>
            ) : (
              <>
                {food.ordersLeft === 0 && (
                  <span className="sold-out">Sold Out</span>
                )}
                {food.cutoffTime && new Date(food.cutoffTime) < new Date() && (
                  <span className="expired-badge">Ordering Period Over</span>
                )}
              </>
            )}
          </div>

          {/* Notice */}
          {food.notice && (
            <div className="modal-notice">
              <strong>Notice:</strong> {food.notice}
            </div>
          )}

          {/* Add to Cart Button */}
          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={food.ordersLeft === 0 || (food.cutoffTime && new Date(food.cutoffTime) < new Date()) || isOwner}
            aria-disabled={food.ordersLeft === 0 || (food.cutoffTime && new Date(food.cutoffTime) < new Date()) || isOwner}
            title={isOwner ? 'You cannot add your own item to cart' : undefined}
          >
            <span className="cart-icon">🛒</span>
            {isOwner ? 'Your Item' : (food.ordersLeft === 0 && (food.cutoffTime && new Date(food.cutoffTime) < new Date())) ? 'Unavailable' : (food.ordersLeft === 0 ? 'Sold Out' : (food.cutoffTime && new Date(food.cutoffTime) < new Date() ? 'Ordering Period Over' : 'Add to Cart'))}
          </button>
        </div>
      </div>

      {/* Fullscreen image overlay */}
      {isImageFullscreen && (
        <div
          className="image-fullscreen-overlay"
          onClick={(e) => { if (e.target.className === 'image-fullscreen-overlay') setIsImageFullscreen(false); }}
        >
          <div className="image-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-fullscreen-close-btn" onClick={() => setIsImageFullscreen(false)}>×</button>

            <button
              className="image-fullscreen-fit-btn"
              onClick={(e) => { e.stopPropagation(); setIsFitToScreen((s) => !s); }}
              aria-pressed={isFitToScreen}
              title={isFitToScreen ? 'Fit to screen (click to show actual size)' : 'Actual size (click to fit to screen)'}
            >
              {isFitToScreen ? 'Fit' : 'Actual'}
            </button>

            <button className="image-fullscreen-nav-btn image-fullscreen-prev" onClick={(e) => { e.stopPropagation(); prevImage(); }} aria-label="Previous image">‹</button>

            <img
              src={images[currentImageIndex]}
              alt={`${food.name} - Image ${currentImageIndex + 1}`}
              className={`image-fullscreen-image ${isFitToScreen ? 'fit' : 'actual'}`}
            />

            <button className="image-fullscreen-nav-btn image-fullscreen-next" onClick={(e) => { e.stopPropagation(); nextImage(); }} aria-label="Next image">›</button>

            <div className="image-fullscreen-caption">{food.name} — {currentImageIndex + 1}/{images.length}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodDetailModal;
